/**
 * The viewport breakpoints, in one place.
 *
 * ## Why this is a TypeScript file and not a CSS token
 *
 * A custom property cannot be used inside a media query. `@media (max-width:
 * var(--bp-md))` is not merely unsupported — it parses as invalid and the whole block is
 * dropped, silently, so the layout is simply wrong with nothing in the console. There is
 * no shipping browser with `@custom-media` either.
 *
 * So the values live here, `useViewport` reads them, and the media queries in
 * `styles/**` mirror them by hand. Hand-mirrored constants drift, which is why
 * `web/tests/breakpoints.test.ts` parses the stylesheets and fails when a query uses a
 * width that is not in this table. That check is what makes the duplication honest.
 *
 * ## What these replace
 *
 * Three uncoordinated values — 640, 820 and 860 — chosen one at a time, with 820 also
 * written as a literal in `App.tsx` to label a WebSocket connection. Nothing tied them
 * together and no two of them meant the same thing about a device.
 */

/**
 * Upper bounds, in pixels.
 *
 * Read as "a phone is anything up to `sm`". `xl` has no upper bound above it; it is the
 * point where the content column stops growing.
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 900,
  lg: 1280,
  xl: 1600,
} as const;

export type BreakpointName = keyof typeof BREAKPOINTS;

/**
 * The three layouts the design actually distinguishes.
 *
 * Deliberately not one bucket per breakpoint: `lg` and `xl` change how wide the content
 * column is, not which controls exist, and a component that branched on them would be
 * making a decision CSS should have made.
 */
export type Viewport = 'phone' | 'tablet' | 'desktop';

export function viewportFor(width: number): Viewport {
  if (width <= BREAKPOINTS.sm) return 'phone';
  if (width <= BREAKPOINTS.md) return 'tablet';
  return 'desktop';
}
